'use client'

import { useEffect, Suspense } from 'react'
import { useSearchParams } from 'next/navigation'
import { setPromoCodeCookie, getPromoCodeCookie } from '@/lib/promo-cookie'

/**
 * Inner component that actually uses useSearchParams
 * Must be wrapped in Suspense for static page generation
 */
function PromoCodeCaptureInner() {
  const searchParams = useSearchParams()
  
  useEffect(() => {
    // Check for ref or promo in URL params
    const refCode = searchParams.get('ref')
    const promoCode = searchParams.get('promo')
    const code = refCode || promoCode
    
    if (code && code.length >= 4) {
      // Only store if we have a valid-looking code
      const existingCode = getPromoCodeCookie()
      
      // Update the cookie if:
      // 1. No existing code, or
      // 2. The URL has a different code (user clicked a new referral link)
      if (!existingCode || existingCode !== code.toUpperCase()) {
        setPromoCodeCookie(code)
      }
    }
  }, [searchParams])
  
  // This component doesn't render anything
  return null
}

/**
 * PromoCodeCapture Component
 * 
 * This invisible component captures promo/referral codes from URL parameters
 * and stores them in a cookie for later use during signup.
 * 
 * Add this component to any page where users might land with a promo code.
 * The code will persist across page navigations until the user signs up.
 */
export function PromoCodeCapture() {
  return (
    <Suspense fallback={null}>
      <PromoCodeCaptureInner />
    </Suspense>
  )
}

